export interface Product {
  ns_product_id: number;
  sku: string | null;
  name: string | null;
  manufacturer: string | null;
  vendor: string | null;
  purchase_price: number | null;
  /** Core charge amount from NetSuite — null when the item has none (BOT-369). */
  core_charge: number | null;
  website_price: number | null;
  on_hand: number;
  item_status: string | null;
  website_visible: string | null;
  inactive: boolean;
  magento_id: number | null;
}

export interface ProductsPage {
  items: Product[];
  total: number;
  page: number;
  per_page: number;
}

export interface ProductFilters {
  search?: string;
  vendor?: string;
  manufacturer?: string;
  purchase_price_min?: number;
  purchase_price_max?: number;
  on_hand_min?: number;
  status?: 'active' | 'inactive' | 'all';
  page?: number;
  per_page?: number;
}

export async function fetchProducts(filters: ProductFilters = {}): Promise<ProductsPage> {
  const params = new URLSearchParams();
  if (filters.search)                          params.set('search', filters.search);
  if (filters.vendor)                          params.set('vendor', filters.vendor);
  if (filters.manufacturer)                    params.set('manufacturer', filters.manufacturer);
  if (filters.purchase_price_min !== undefined) params.set('purchase_price_min', String(filters.purchase_price_min));
  if (filters.purchase_price_max !== undefined) params.set('purchase_price_max', String(filters.purchase_price_max));
  if (filters.on_hand_min !== undefined)        params.set('on_hand_min', String(filters.on_hand_min));
  if (filters.status)                          params.set('status', filters.status);
  if (filters.page)                            params.set('page', String(filters.page));
  if (filters.per_page)                        params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/products?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Products fetch failed: ${res.status}`);
  return res.json() as Promise<ProductsPage>;
}

import type { Check } from './checks';

export type ProductDetail = Record<string, unknown> & {
  ns_product_id: number;
  sku: string | null;
  name: string | null;
  /** Latest greenlight check for this item (null — never checked). */
  price_check?: Check | null;
  /** Check history, newest first (capped at 50). */
  history?: Check[];
};

/** Read one dynamic tbnsdata column as display text.
 *  `ProductDetail` is an open record (the endpoint does `SELECT *`), so every
 *  column outside the declared fields arrives as `unknown`. */
export function detailText(detail: ProductDetail | null | undefined, field: string): string | null {
  const v = detail?.[field];
  if (v == null || v === '') return null;
  return String(v);
}

/** Read one dynamic tbnsdata column as a number, for the money formatters.
 *  Returns null — never NaN — when the column is absent, null, or not numeric,
 *  so callers can hand the result straight to `fmtPrice` and get "—". */
export function detailNumber(detail: ProductDetail | null | undefined, field: string): number | null {
  const v = detail?.[field];
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export async function fetchProductDetail(nsProductId: number): Promise<ProductDetail> {
  const res = await fetch(`/api/v1/products/${nsProductId}`, { credentials: 'include' });
  if (res.status === 404) throw new Error('Product not found');
  if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
  return res.json() as Promise<ProductDetail>;
}

// ── Item freshness (BOT-484) ────────────────────────────────────────────────

export type FreshnessBand = 'green' | 'amber' | 'red' | 'gray';
export type AlertColor = 'amber' | 'red' | 'gray';

export interface FreshnessValue {
  /** confirmed — a data source read it; never — it has a lane but no read yet;
   *  no_source — the item's vendor has no lane for this value. */
  state: 'confirmed' | 'never' | 'no_source';
  confirmed_at: string | null;
  /** Calendar day "YYYY-MM-DD" the API chose for display (ET, CDK by file date). */
  confirmed_day: string | null;
  /** Whole 24-hour periods — drives the band (server side), same rule as the dashboard. */
  age_days: number | null;
  /** Calendar days in ET since confirmed_day — what the UI prints ("7 days ago"). */
  days_ago: number | null;
  band: FreshnessBand;
  /** A check for this value differs from NetSuite and waits in Review. */
  pending_review: boolean;
}

export interface FreshnessAlertPart {
  value: 'price' | 'home_value';
  reason: 'old' | 'never' | 'in_review';
  days_ago: number | null;
  pending_review: boolean;
  color: AlertColor;
}

export interface ItemFreshness {
  as_of: string;
  in_scope: boolean;
  price: FreshnessValue;
  home_value: FreshnessValue;
  alert: { color: AlertColor; parts: FreshnessAlertPart[] } | null;
}

export async function fetchItemFreshness(nsProductId: number): Promise<ItemFreshness> {
  const res = await fetch(`/api/v1/products/${nsProductId}/freshness`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Freshness fetch failed: ${res.status}`);
  return res.json() as Promise<ItemFreshness>;
}
